import { useMemo, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useAuditLogEnabled } from "datasets/hooks/useAuditLogEnabled";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import {
  AlertCircle,
  BarChart3,
  BookOpen,
  Files,
  KeyRound,
  LayoutDashboard,
  ScrollText,
  Settings,
  Sparkles,
  Table2,
  UserRoundPlus,
  Users,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { SectionNavLink } from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useCanViewCohortExplorer } from "~/pages/manifold-nextgen/hooks/useCanViewCohortExplorer";
import { useCohortExplorerEnabled } from "~/pages/manifold-nextgen/hooks/useCohortExplorerEnabled";
import { useDatasetShare } from "~/pages/manifold-nextgen/hooks/useDatasetShare";
import { ROUTE_404, ROUTE_NEXTGEN_DATASETS } from "~/router/constants";

const BASE_NAV_ITEMS = [
  {
    label: "Overview",
    path: "overview",
    icon: <LayoutDashboard className="size-4" />,
  },
  {
    label: "Dashboards",
    path: "dashboards",
    icon: <BarChart3 className="size-4" />,
  },
] as const;

export const DatasetDetail = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const { data: dataset, error, isLoading } = useDataset();
  const canEdit = useDatasetPermission("edit:catalog-dataset");
  const cohortExplorerEnabled = useCohortExplorerEnabled();
  const canViewCohortExplorer = useCanViewCohortExplorer(dataset);
  const currentUser = useUser();
  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useDatasetShare();

  const [shareOpen, setShareOpen] = useState(false);

  const auditLogEnabled = useAuditLogEnabled();

  useDocumentTitle(dataset?.title ?? "");

  const headerBreadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: ROUTE_NEXTGEN_DATASETS,
        entityName: dataset?.title,
      }),
    [dataset?.title]
  );

  // Rename / copy / preview / delete moved to the Settings page reached from the
  // sidebar; the header keeps only the Share action.
  const headerActions = useMemo<[React.ReactNode?]>(() => {
    if (!dataset) {
      return [];
    }
    const shareNode = canEdit ? (
      <Button key="share-dataset" size="sm" className="text-sm" onClick={() => setShareOpen(true)}>
        <UserRoundPlus className="size-4" />
        Share
      </Button>
    ) : undefined;

    return [shareNode];
  }, [dataset, canEdit]);

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs, actions: headerActions });

  if (error && (error as { status?: number }).status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading dataset" className="flex min-h-full">
        <div className="w-56 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="mx-1 my-1 h-8 rounded-md" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load dataset</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <SectionSidebar ariaLabel="Dataset sections">
          <div className="flex flex-col gap-1 px-4 py-3">
            {BASE_NAV_ITEMS.map(({ label, path, icon }) => (
              <SectionNavLink key={path} to={path} end icon={icon} label={label} />
            ))}
            {dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink
                to="tables"
                end
                icon={<Table2 className="size-4" />}
                label="Tables"
                badge={dataset.table_count}
              />
            )}
            {dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink to="files" icon={<Files className="size-4" />} label="Files" />
            )}
            {dataset.aiAgentsEnabled && dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink
                to="ai-context"
                icon={<BookOpen className="size-4" />}
                label="Context"
              />
            )}
            {dataset.aiAgentsEnabled && dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink
                to="ai-skills"
                icon={<Sparkles className="size-4" />}
                label="Skills"
              />
            )}
            {canViewCohortExplorer && (
              <SectionNavLink
                to="cohort-explorer"
                icon={<Users className="size-4" />}
                label="Cohort Explorer"
              />
            )}
            {cohortExplorerEnabled &&
              (dataset.access_level === DatasetAccessLevel.OWNER ||
                dataset.access_level === DatasetAccessLevel.PREVIEW) && (
                <SectionNavLink
                  to="access-requests"
                  icon={<KeyRound className="size-4" />}
                  label="Access Requests"
                />
              )}
            {auditLogEnabled && dataset.access_level === DatasetAccessLevel.OWNER && (
              <SectionNavLink
                to="audit-log"
                icon={<ScrollText className="size-4" />}
                label="Changelog"
              />
            )}
            <SectionNavLink
              to="settings"
              end
              icon={<Settings className="size-4" />}
              label="Settings"
            />
          </div>
        </SectionSidebar>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={dataset} />
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        resourceLabel="Dataset"
        resourceName={dataset.title}
        collaborators={collaborators}
        collaboratorsLoading={collaboratorsLoading}
        collaboratorsError={collaboratorsError}
        accessLevels={accessLevels}
        defaultAccessLevel={defaultAccessLevel}
        onShare={handleShare}
        currentUserEmail={currentUser?.email}
      />
    </>
  );
};
